import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store/store";
import { CommonApiData } from "@type/common";
import { BuildVideoReferenceScriptRequest, fetchReferenceScript } from "@apis/videoCreatingService";
import { message } from "antd";
import { t } from "i18next";
import type { SetLibraryInitPayload } from "./type";
import { processErrorMessage } from "@utils/errrorMessage";
import {
  fetchCreateOption,
  type FetchCreateOptionResponse,
  FetchParseProductImagesResponse,
  getVideoCreationJobDetail,
} from "@apis/http/api";
import { VideoCreationRequestInternalModelId } from "@apis/generated/openapi";
import { transformAbbreviationCountry2FullCountry } from "@utils/util";

export interface ProductImage {
  id: string | number;
  url: string;
  assetId?: number;
  /** 图片标签 */
  description: string;
  /** 用于本地反显 */
  base64?: string;
  isChangedUrl: boolean;
  /** 是否已通过AI分析 */
  aiAnalyzed: boolean;
  /** 是否需要人脸遮罩 */
  needsFaceMasking?: boolean;
}

export interface BasicInfoData {
  linkSource: FetchParseProductImagesResponse["linkSource"];
  images: ProductImage[];
  productTitle: string;
  /** 商品分类Category */
  productCategory: string;
}

export interface ProductInfoData {
  /** @deprecated 使用 BasicInfoData.productCategory 代替，保留用于兼容旧数据 */
  category: string;
  sellingCountry: string;
  language: string;
  sellingPoints: CommonApiData;
  creationDescription?: string;
}

// 视频参数类型定义
export type AspectRatio = string;
export type VideoDuration = number;
export type VideoQuantity = number;
export type VideoResolution = string;
export type VideoModel = string;

export interface DescParamsData {
  aspectRatio: AspectRatio;
  duration: VideoDuration;
  quantity: VideoQuantity;
  resolution: VideoResolution;
  /** 内部模型 ID，比如 v31、sr2l、sr2、sd2f、sd2、sr20 */
  internalModelId: VideoCreationRequestInternalModelId | undefined;
  countDown: number;
  videoScriptStatus: Status;
  originScript: string; // 原始脚本
  videoScript: string; // 处理后的脚本
  totalCost: number;
  /** 上次请求积分时的参数指纹，用于判断是否需要重新请求 */
  creditCacheKey: string;
}

export interface CreateOptionsData extends Omit<FetchCreateOptionResponse, "rules"> {
  rules: {
    duration_resolution: Record<string | number, (string | number)[]>;
    duration_model: Record<string | number, string[]>;
    model_resolution: Record<string | number, string[]>;
  };
}

export interface VideoCreatingState {
  stepStatus: {
    basicInfoInit: boolean;
    productInfoInit: boolean;
    descParamsInit: boolean;
    currentStep: number;
  };
  basicInfo: BasicInfoData;
  productInfo: ProductInfoData;
  descParams: DescParamsData;
  createOptions: CreateOptionsData;
  productId: string; // 从库中选择的商品 ID
  country: string;
  jobId: string;
  videoGenerating: boolean;
  jobDetailLoading: boolean;
}

const keepOrDefault = <T extends { default?: boolean; value: any }>(
  options: T[],
  current: any,
  preferApiDefault = false,
) => {
  const apiDefault = options.find((o) => o.default);
  if (preferApiDefault && apiDefault) return apiDefault.value;
  if (options.some((o) => o.value === current)) return current;
  return (apiDefault ?? options[0])?.value;
};

const toStringArray = (values?: (string | number)[]) => values?.map((value) => String(value)) ?? [];

export const filterResolutionOptionsByRules = <T extends { value: string | number }>(
  resolutionOptions: T[],
  allowedResolutionsByDuration?: (string | number)[],
  allowedResolutionsByModel?: (string | number)[],
) => {
  const byDurationSet = allowedResolutionsByDuration?.length
    ? new Set(toStringArray(allowedResolutionsByDuration))
    : null;
  const byModelSet = allowedResolutionsByModel?.length ? new Set(toStringArray(allowedResolutionsByModel)) : null;

  if (!byDurationSet && !byModelSet) return resolutionOptions;

  return resolutionOptions.filter((option) => {
    const value = String(option.value);
    const inDuration = byDurationSet?.has(value) ?? false;
    const inModel = byModelSet?.has(value) ?? false;
    return inDuration || inModel;
  });
};

const normalizeDescParamsByRules = (
  descParams: DescParamsData,
  createOptions: CreateOptionsData,
  preferApiDefault = false,
): DescParamsData => {
  const next = { ...descParams };
  const { durationOptions, resolutionOptions, modelOptions, rules } = createOptions;

  if (durationOptions.length) {
    next.duration = keepOrDefault(durationOptions, next.duration, preferApiDefault);
  }
  if (modelOptions.length) {
    next.internalModelId = keepOrDefault(modelOptions, next.internalModelId, preferApiDefault);
  }
  if (resolutionOptions.length) {
    next.resolution = keepOrDefault(resolutionOptions, next.resolution, preferApiDefault);
  }

  // duration_resolution 支持双向映射：resolution -> duration[]
  const allowedDurations = rules?.duration_resolution?.[next.resolution];
  const allowedDurationValues = allowedDurations?.map((value) => Number(value)).filter((value) => !Number.isNaN(value));
  if (allowedDurationValues?.length && !allowedDurationValues.includes(next.duration)) {
    next.duration = allowedDurationValues[0];
  }

  const allowedModels = rules?.duration_model?.[next.duration];
  if (allowedModels?.length && next.internalModelId && !allowedModels.includes(next.internalModelId)) {
    next.internalModelId = allowedModels[0] as VideoCreationRequestInternalModelId;
  }

  const allowedResolutionsByDuration = rules?.duration_resolution?.[next.duration];
  const allowedResolutionsByModel = next.internalModelId ? rules?.model_resolution?.[next.internalModelId] : undefined;
  const supportedResolutionOptions = filterResolutionOptionsByRules(
    resolutionOptions,
    allowedResolutionsByDuration,
    allowedResolutionsByModel,
  );

  if (supportedResolutionOptions.length) {
    if (!supportedResolutionOptions.some((option) => option.value === next.resolution)) {
      next.resolution = supportedResolutionOptions[0].value;
    }
    return next;
  }

  const fallbackByDuration = toStringArray(allowedResolutionsByDuration);
  if (fallbackByDuration.length && !fallbackByDuration.includes(next.resolution)) {
    next.resolution = fallbackByDuration[0];
    return next;
  }

  if (allowedResolutionsByModel?.length && !allowedResolutionsByModel.includes(next.resolution)) {
    next.resolution = allowedResolutionsByModel[0];
  }

  return next;
};

const initialState: VideoCreatingState = {
  stepStatus: {
    basicInfoInit: false,
    productInfoInit: false,
    descParamsInit: false,
    currentStep: 0,
  },
  basicInfo: {
    images: [],
    linkSource: undefined,
    productTitle: "",
    productCategory: "",
  },
  productInfo: {
    category: "",
    sellingCountry: "",
    language: "en",
    sellingPoints: { status: "idle", data: [], error: null },
  },
  descParams: {
    aspectRatio: "RATIO_9_16",
    duration: 8,
    quantity: 1,
    resolution: "",
    internalModelId: undefined,
    countDown: 180,
    videoScriptStatus: "idle",
    originScript: "",
    videoScript: "",
    totalCost: 0,
    creditCacheKey: "",
  },
  createOptions: {
    aspectRatioOptions: [],
    durationOptions: [],
    quantityOptions: [],
    resolutionOptions: [],
    modelOptions: [],
    rules: { duration_resolution: {}, duration_model: {}, model_resolution: {} },
  },
  jobId: "",
  productId: "",
  country: "",
  videoGenerating: false,
  jobDetailLoading: false,
};

// 获取视频创建选项
export const fetchCreateOptions = createAsyncThunk(
  "videoCreating/fetchCreateOptions",
  async (params: { videoLanguage: string }) => {
    return await fetchCreateOption(params);
  },
);

// 从 jobId 获取任务详情（Edit Video 场景）
export const fetchJobDetail = createAsyncThunk("videoCreating/fetchJobDetail", async (jobId: string | number) => {
  return await getVideoCreationJobDetail({ jobId: Number(jobId) });
});

let countdownTimer: ReturnType<typeof setInterval> | null = null;

export const clearCountdownTimer = () => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
};

export const fetchReferenceScriptData = createAsyncThunk(
  "videoCreating/fetchReferenceScript",
  async (data: BuildVideoReferenceScriptRequest, { dispatch, getState }) => {
    clearCountdownTimer();
    countdownTimer = setInterval(() => {
      const state = getState() as RootState;
      const current = state.videoCreating.descParams.countDown;
      if (current > 0) {
        dispatch(setDescParams({ countDown: current - 1 }));
      }
    }, 1000);

    try {
      return await fetchReferenceScript(data);
    } catch (e: any) {
      const processed = processErrorMessage(e);
      if (processed) {
        return;
      }
      message.error(typeof e?.message === "string" ? t(e.message) : t(e?.code || "server error"));
      // 强制走rejected分支
      throw e;
    } finally {
      clearCountdownTimer();
    }
  },
);

const videoCreatingSlice = createSlice({
  name: "videoCreating",
  initialState,
  reducers: {
    updateParams: (state, action: any) => {
      Object.entries(action.payload).forEach(([key, value]) => {
        if (key in state) {
          // @ts-expect-error: Dynamic assignment based on defined state keys
          state[key] = value;
        }
      });
    },
    resetToInit: (state) => {
      clearCountdownTimer();
      Object.keys(initialState).forEach((key) => {
        (state as any)[key] = (initialState as any)[key];
      });
    },
    updateReady: (
      state,
      action: PayloadAction<{
        productId: string;
        country: string;
      }>,
    ) => {
      state.productId = action.payload.productId;
      state.country = action.payload.country;
    },
    setStepStatus: (state, action: any) => {
      state.stepStatus = { ...state.stepStatus, ...action.payload };
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.stepStatus.currentStep = action.payload;
    },
    setBasicInfo: (state, action: PayloadAction<Partial<BasicInfoData>>) => {
      state.basicInfo = { ...state.basicInfo, ...action.payload };
    },
    setProductInfo: (state, action: PayloadAction<Partial<ProductInfoData>>) => {
      state.productInfo = { ...state.productInfo, ...action.payload };
    },
    setSellingPoints: (state, action: PayloadAction<CommonApiData>) => {
      state.productInfo.sellingPoints = action.payload;
    },
    setDescParams: (state, action: PayloadAction<Partial<DescParamsData>>) => {
      const mergedDescParams = { ...state.descParams, ...action.payload };
      const shouldNormalize =
        "duration" in action.payload || "resolution" in action.payload || "internalModelId" in action.payload;
      state.descParams = shouldNormalize
        ? normalizeDescParamsByRules(mergedDescParams, state.createOptions)
        : mergedDescParams;
    },
    // 从库中选择商品后，将数据拆分写入 basicInfo 和 productInfo
    setLibraryInit: (state, action: PayloadAction<SetLibraryInitPayload>) => {
      const { id, country, productTitle, images, priCateName, secCateName, terCateName } =
        action.payload?.product || {};
      const categoryParts = [priCateName, secCateName, terCateName].filter(Boolean);
      const categoryText = categoryParts.join(" > ");
      // 设置商品 ID
      state.productId = id ?? "";
      state.country = country || "United States";
      // 设置 basicInfo
      state.basicInfo.productTitle = productTitle ?? "";
      state.basicInfo.images = (images || []).map((image) => ({
        id: `product-image-${image.assetId || 0}`,
        assetId: image.assetId,
        url: image.imageUrl,
        description: "",
        isChangedUrl: true,
        // 外面传入的已经过滤了true的
        success: true,
        aiAnalyzed: image.aiAnalyzed ?? false,
      })) as BasicInfoData["images"];
      // 拼接分类信息
      state.basicInfo.productCategory = categoryText;
      state.productInfo.category = categoryText;
    },
    setVideoGenerating: (state, action: PayloadAction<boolean>) => {
      state.videoGenerating = action.payload;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreateOptions.fulfilled, (state, action) => {
        if (!action.payload) return;
        const isFirstOptionsLoad =
          state.createOptions.aspectRatioOptions.length === 0 &&
          state.createOptions.durationOptions.length === 0 &&
          state.createOptions.quantityOptions.length === 0 &&
          state.createOptions.resolutionOptions.length === 0 &&
          state.createOptions.modelOptions.length === 0;
        const shouldPreferApiDefault = isFirstOptionsLoad;
        const {
          aspectRatioOptions = [],
          durationOptions = [],
          quantityOptions = [],
          resolutionOptions = [],
          modelOptions = [],
          rules = {},
        } = action.payload;

        state.createOptions = {
          aspectRatioOptions,
          durationOptions,
          quantityOptions,
          resolutionOptions,
          modelOptions,
          rules: rules as CreateOptionsData["rules"],
        };

        if (aspectRatioOptions.length) {
          state.descParams.aspectRatio = keepOrDefault(
            aspectRatioOptions,
            state.descParams.aspectRatio,
            shouldPreferApiDefault,
          );
        }
        if (quantityOptions.length) {
          state.descParams.quantity = keepOrDefault(quantityOptions, state.descParams.quantity, shouldPreferApiDefault);
        }
        state.descParams = normalizeDescParamsByRules(state.descParams, state.createOptions, shouldPreferApiDefault);
      })
      .addCase(fetchReferenceScriptData.pending, (state) => {
        state.descParams.countDown = 180;
        state.descParams.videoScriptStatus = "loading";
      })
      .addCase(fetchReferenceScriptData.fulfilled, (state, action: any) => {
        state.descParams.originScript = action.payload;
        state.descParams.videoScript = action.payload;
        state.descParams.videoScriptStatus = "succeeded";
        state.descParams.countDown = 0;
        message.success(t("Script generation successfully"));
      })
      .addCase(fetchReferenceScriptData.rejected, (state) => {
        state.descParams.videoScriptStatus = "failed";
        state.descParams.countDown = 0;
        message.error(t("Script generation failed"));
      })
      .addCase(fetchJobDetail.pending, (state) => {
        state.jobDetailLoading = true;
      })
      .addCase(fetchJobDetail.fulfilled, (state, action) => {
        state.jobDetailLoading = false;
        const { input, id } = action.payload;
        if (!input) return;
        const sellingCountry = transformAbbreviationCountry2FullCountry(input.sellingCountry || "");
        state.productId = input.productId || "";
        state.country = "country" in input ? input.country || "" : "";
        state.jobId = String(id || "");

        // Step 1: basicInfo
        state.basicInfo.productTitle = input.productTitle || "";
        state.basicInfo.images = (input.imageInfos || []).map((img) => ({
          id: img.assetId,
          url: img.imageUrl,
          description: img.description || "",
          isChangedUrl: true,
          // TODO(leon): check if the image is ai analyzed
          aiAnalyzed: true,
        }));
        state.stepStatus.basicInfoInit = true;

        // Step 2: productInfo
        const categoryNames = (input.categoryNames ?? []).filter(Boolean).join(" > ");
        state.productInfo.category = categoryNames;
        state.basicInfo.productCategory = categoryNames;
        state.productInfo.creationDescription =
          "creationDescription" in input ? (input.creationDescription ?? undefined) : undefined;
        state.productInfo.sellingCountry = sellingCountry ?? "";
        state.productInfo.language = "language" in input ? (input.language ?? "en") : "en";

        const hasSellingPoints = Array.isArray(input.sellingPoints) && input.sellingPoints.length > 0;
        if (hasSellingPoints) {
          state.productInfo.sellingPoints = {
            status: "succeeded",
            data: (input.sellingPoints as any[]).map((sp) => ({
              checked: true,
              ...(typeof sp === "string" ? { keyWord: sp } : sp),
            })),
            error: null,
          };
          state.stepStatus.productInfoInit = true;
        } else {
          state.productInfo.sellingPoints = { status: "idle", data: [], error: null };
          state.stepStatus.productInfoInit = false;
        }

        // Step 3: descParams
        if (input.aspectRatio) state.descParams.aspectRatio = input.aspectRatio;
        if (input.duration)
          state.descParams.duration = typeof input.duration === "number" ? input.duration : Number(input.duration);
        if (input.quantity) state.descParams.quantity = input.quantity;
        if (input.resolution) state.descParams.resolution = input.resolution;
      })
      .addCase(fetchJobDetail.rejected, (state) => {
        state.jobDetailLoading = false;
      });
  },
});

export const {
  setStepStatus,
  updateParams,
  resetToInit,
  updateReady,
  setCurrentStep,
  setBasicInfo,
  setProductInfo,
  setSellingPoints,
  setDescParams,
  setLibraryInit,
  setVideoGenerating,
  resetState,
} = videoCreatingSlice.actions;

export const selectCurrentStep = (state: RootState) => state.videoCreating.stepStatus.currentStep;
export const selectStepStatus = (state: RootState) => state.videoCreating.stepStatus;
export const selectBasicInfo = (state: RootState) => state.videoCreating.basicInfo;
export const selectProductInfo = (state: RootState) => state.videoCreating.productInfo;
// export const selectProductId = (state: RootState) => state.videoCreating.productId;
export const selectSellingPoints = (state: RootState) => state.videoCreating.productInfo.sellingPoints;
export const selectVideoParams = (state: RootState) => state.videoCreating.descParams;
export const selectVideoCreatingParams = (state: RootState) => {
  return {
    basicInfo: state.videoCreating.basicInfo,
    productInfo: state.videoCreating.productInfo,
    descParams: state.videoCreating.descParams,
    productId: state.videoCreating.productId,
    country: state.videoCreating.country,
    jobId: state.videoCreating.jobId,
  };
};
export const selectCreateOptions = (state: RootState) => state.videoCreating.createOptions;
export const selectVideoGenerating = (state: RootState) => state.videoCreating.videoGenerating;
export const selectJobDetailLoading = (state: RootState) => state.videoCreating.jobDetailLoading;
export default videoCreatingSlice.reducer;
